/**
 * VIB-2979 (B6): hvilke kilder fra kundekortet AI skal bruke for et tilbud.
 *
 * Kildene er kundens `customer_activities`. Godkjente møtereferater lagres der
 * som `type = 'summary'` (20261017195000_crm_meeting_summary.sql), så ingen
 * egen kilde for referater trengs. Referater og notater er med som standard,
 * e-post, samtaler, møter og SMS ikke.
 *
 * `offer_workspaces.ai_sources` lagrer bare valg som avviker fra standarden.
 * Da dukker nye aktiviteter opp med riktig standard uten at noe skrives.
 */
import type { CustomerActivityType, DBCustomerActivity } from "@/lib/db-types";

export interface AiSourceChoice {
  type: "customer_activity";
  id: string;
  included: boolean;
}

export const DEFAULT_INCLUDED_ACTIVITY_TYPES: readonly CustomerActivityType[] = ["summary", "note"];

/** Planlagte, avbrutte og feilede aktiviteter er ikke noe som har skjedd. */
const USABLE_STATUSES = new Set(["logged", "sent", "received"]);

export function isUsableSource(a: Pick<DBCustomerActivity, "status" | "subject" | "body">): boolean {
  return USABLE_STATUSES.has(a.status) && !!((a.body ?? "").trim() || (a.subject ?? "").trim());
}

export function includedByDefault(type: CustomerActivityType): boolean {
  return DEFAULT_INCLUDED_ACTIVITY_TYPES.includes(type);
}

/** Leser `ai_sources` fra databasen og hopper over rader med feil form. */
export function parseAiSources(raw: unknown): AiSourceChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const { type, id, included } = item as Record<string, unknown>;
    if (type !== "customer_activity" || typeof id !== "string" || typeof included !== "boolean") return [];
    return [{ type, id, included }];
  });
}

export interface ResolvedSource<A> {
  activity: A;
  included: boolean;
}

export function resolveAiSources<A extends Pick<DBCustomerActivity, "id" | "type" | "status" | "subject" | "body">>(
  activities: readonly A[],
  choices: readonly AiSourceChoice[],
): ResolvedSource<A>[] {
  const byId = new Map(choices.map((c) => [c.id, c.included]));
  return activities
    .filter(isUsableSource)
    .map((activity) => ({
      activity,
      included: byId.get(activity.id) ?? includedByDefault(activity.type),
    }));
}

/** Nytt valg for én kilde. Valg som er lik standarden fjernes. */
export function setAiSourceChoice(
  choices: readonly AiSourceChoice[],
  activity: Pick<DBCustomerActivity, "id" | "type">,
  included: boolean,
): AiSourceChoice[] {
  const rest = choices.filter((c) => c.id !== activity.id);
  if (included === includedByDefault(activity.type)) return rest;
  return [...rest, { type: "customer_activity", id: activity.id, included }];
}
